import React, { useState } from "react";
import { X, Lock, Globe2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useSpamGuard } from "@/lib/spamGuard";

const inputCls = "w-full bg-white border border-aviana-border rounded-xl px-4 py-3 text-[15px] text-aviana-ink outline-none focus:border-aviana-plum transition-colors";

function F({ label, k, f, set, half, required = true, type = "text", auto }) {
  return (
    <label className={`block ${half ? "" : "sm:col-span-2"}`}>
      <span className="block text-[10px] tracking-[0.2em] uppercase text-aviana-plum mb-1.5">{label}{!required && " (optional)"}</span>
      <input type={type} required={required} autoComplete={auto} value={f[k]} onChange={(e) => set(k, e.target.value)} className={inputCls} data-testid={`guest-${k}`} />
    </label>
  );
}

export default function GuestCheckoutModal({ slug, price, user, onClose }) {
  const [f, setF] = useState({
    full_name: user?.name || "", email: user?.email || "", street: "", street2: "",
    city: "", state: "", postal_code: "", country: "United States",
  });
  const [busy, setBusy] = useState(false);
  const { honeypot, guard } = useSpamGuard();
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/payments/guest-checkout", { ...f, ...guard(), offering_slug: slug, origin_url: window.location.origin });
      if (data?.url) window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not start checkout");
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-aviana-ink/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="guest-checkout-modal">
      <form onSubmit={submit} className="relative bg-aviana-cream rounded-[2rem] w-full max-w-xl max-h-[94vh] overflow-y-auto shadow-2xl p-7 md:p-10">
        {honeypot}
        <button type="button" onClick={onClose} className="absolute right-5 top-5 text-aviana-muted hover:text-aviana-ink" data-testid="guest-close"><X size={20} /></button>
        <p className="overline-plum mb-2">Enroll · ${price}</p>
        <h2 className="font-display text-3xl md:text-4xl text-aviana-ink leading-tight">Where should we send <em className="italic font-serif gold-text">your manual?</em></h2>
        <p className="mt-3 text-[14px] text-aviana-muted leading-relaxed">No account needed — after payment we'll email you a link to set up your login and step into the course.</p>
        <div className="mt-7 grid sm:grid-cols-2 gap-4">
          <F label="Full name" k="full_name" f={f} set={set} half auto="name" />
          <F label="Email" k="email" type="email" f={f} set={set} half auto="email" />
          <F label="Street address" k="street" f={f} set={set} auto="address-line1" />
          <F label="Apartment, suite, etc." k="street2" f={f} set={set} required={false} auto="address-line2" />
          <F label="City" k="city" f={f} set={set} half auto="address-level2" />
          <F label="State / Province" k="state" f={f} set={set} half auto="address-level1" />
          <F label="Postal code" k="postal_code" f={f} set={set} half auto="postal-code" />
          <F label="Country" k="country" f={f} set={set} half auto="country-name" />
        </div>
        <p className="mt-5 flex gap-2.5 items-start text-[13px] text-aviana-inkSoft bg-aviana-goldSoft/15 border border-aviana-gold/30 rounded-xl px-4 py-3" data-testid="guest-intl-notice">
          <Globe2 size={16} className="text-aviana-gold flex-shrink-0 mt-0.5" />
          Shipping outside the United States? Additional shipping fees may apply — Holly will follow up by email if so.
        </p>
        <button type="submit" disabled={busy} className="btn-gold w-full justify-center mt-7 !py-4" data-testid="guest-continue">
          <Lock size={14} /> {busy ? "Opening secure checkout…" : "Continue to secure payment"}
        </button>
      </form>
    </div>
  );
}
